import React from 'react';
import { LessonStep } from '../types';
import { Check, ChevronRight, Lock } from 'lucide-react';

interface LevelProgressBarProps {
  level: number;
  step: LessonStep;
  totalLevels: number;
}

const STEPS: { step: LessonStep; label: string }[] = [
  { step: 1, label: 'Intro' },
  { step: 2, label: 'Repeat' },
  { step: 3, label: 'Listen' },
  { step: 4, label: 'Speak' },
  { step: 5, label: 'Practice' },
  { step: 6, label: 'Switch' },
  { step: 7, label: 'Review' },
];

const LevelProgressBar: React.FC<LevelProgressBarProps> = ({ level, step }) => {
  return (
    <div className="bg-white/80 backdrop-blur-sm shadow-sm px-4 py-3">
      <div className="max-w-2xl mx-auto">
        {/* Level label */}
        <p className="text-xs font-semibold text-gray-400 text-center mb-2">
          Level {level}
        </p>

        {/* Step journey path */}
        <div className="flex items-start justify-center">
          {STEPS.map((s, index) => {
            const isCompleted = s.step < step;
            const isCurrent = s.step === step;
            const isLocked = s.step > step;
            const isLast = index === STEPS.length - 1;

            // Arrow is teal if both sides are completed
            const nextCompleted = !isLast && STEPS[index + 1].step < step;
            const arrowCompleted = isCompleted && nextCompleted;

            return (
              <React.Fragment key={s.step}>
                <div className="flex flex-col items-center">
                  {/* Current indicator dot */}
                  {isCurrent && (
                    <div className="w-1 h-1 rounded-full bg-orange-400 mb-1" />
                  )}
                  {!isCurrent && <div className="h-[8px]" />}

                  {/* Step circle */}
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                      ${isCompleted
                        ? 'bg-teal-400 text-white shadow-sm'
                        : isCurrent
                        ? 'bg-white shadow-md ring-2 ring-orange-300 text-orange-500'
                        : 'bg-gray-100 border border-dashed border-gray-300 text-gray-300'
                      }
                    `}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4" strokeWidth={3} />
                    ) : isLocked ? (
                      <Lock className="w-3 h-3" strokeWidth={2} />
                    ) : (
                      s.step
                    )}
                  </div>

                  {/* Step label */}
                  <span
                    className={`
                      mt-1 text-[10px] font-medium
                      ${isCompleted
                        ? 'text-teal-600'
                        : isCurrent
                        ? 'text-orange-500'
                        : 'text-gray-300'
                      }
                    `}
                  >
                    {s.label}
                  </span>
                </div>

                {/* Arrow connector */}
                {!isLast && (
                  <div className="flex items-center h-8 mx-0.5 mt-[8px]">
                    <ChevronRight
                      className={`w-3 h-3 ${arrowCompleted ? 'text-teal-300' : 'text-gray-200'}`}
                      strokeWidth={2}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LevelProgressBar;
